import { useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from '../services/toastService'
import { useTranslation } from 'react-i18next'

/**
 * Custom hook for spot mode activation logic
 * Handles user authentication checks and entity/location prefilling
 * Wraps the base handler from useSpotMode to add entity detail page logic
 * 
 * @param {Object} params - Configuration parameters
 * @param {Object} params.user - Current user object
 * @param {boolean} params.isImmersiveMode - Whether in immersive mode
 * @param {Function} params.onTabChange - Tab change handler (immersive mode)
 * @param {Function} params.baseHandleSpotModeActivation - Base activation handler from useSpotMode
 * @param {Object} params.entityDetails - Entity details for prefilling
 * @returns {Object} - { handleSpotModeActivation }
 */
export function useSpotModeActivation({
  user,
  isImmersiveMode,
  onTabChange,
  baseHandleSpotModeActivation,
  entityDetails
}) {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const handleSpotModeActivation = useCallback((prefilledEntity = null, prefilledLocation = null) => {
    if (!user) {
      toast.info(t('toast.info.loginRequired'))
      if (isImmersiveMode && onTabChange) {
        onTabChange('auth')
      } else {
        navigate('/login', { state: { from: window.location.pathname } })
      }
      return
    }
    
    // If no prefilled entity is provided but we're viewing an entity detail page, use that entity
    const entityToUse = prefilledEntity || entityDetails
    const locationToUse = prefilledLocation || (entityDetails && entityDetails.current_latitude && entityDetails.current_longitude ? {
      lat: entityDetails.current_latitude,
      lng: entityDetails.current_longitude
    } : null)
    
    // Call the base handler which includes analytics tracking
    baseHandleSpotModeActivation(entityToUse, locationToUse)
  }, [user, navigate, baseHandleSpotModeActivation, isImmersiveMode, onTabChange, entityDetails])

  return { handleSpotModeActivation }
}
